import {
    type AccessGroup,
    type AccessUser,
    listAccessGroups,
    listAccessUsers
} from '@/lib/dashboards-api';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import Alert from '@/components/Alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PillTabsList } from '@/components/ui/pill-tabs-list';
import { SearchBar } from '@/components/ui/search-bar';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';

const PAGE_SIZE = 10;

const TABS = [
    { value: 'users', label: 'User access' },
    { value: 'groups', label: 'Group access' },
    { value: 'organization', label: 'Organization' }
];

export function OrganizationArtifactsNotice() {
    return (
        <Alert variant="info" className="p-3">
            People you give access to will see this dashboard on the
            Organization artifacts page, as long as it is shared there.
        </Alert>
    );
}

function Pager({
    page,
    total,
    onPageChange
}: {
    page: number;
    total: number;
    onPageChange: (page: number) => void;
}) {
    return (
        <div className="flex justify-end items-center gap-2 mt-3 text-muted-foreground text-sm">
            <span className="tabular-nums">
                {total === 0
                    ? 'No results'
                    : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(
                          page * PAGE_SIZE,
                          total
                      )} of ${total}`}
            </span>
            <div className="flex gap-1">
                <Button
                    variant="tertiary"
                    size="icon-sm"
                    aria-label="Previous page"
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                >
                    <ChevronLeft />
                </Button>
                <Button
                    variant="tertiary"
                    size="icon-sm"
                    aria-label="Next page"
                    disabled={page * PAGE_SIZE >= total}
                    onClick={() => onPageChange(page + 1)}
                >
                    <ChevronRight />
                </Button>
            </div>
        </div>
    );
}

type AccessFilter = 'All' | 'Yes' | 'No';

function FilterSelect({
    value,
    onChange
}: {
    value: AccessFilter;
    onChange: (value: AccessFilter) => void;
}) {
    return (
        <Select
            value={value}
            onValueChange={(next) => onChange(next as AccessFilter)}
        >
            <SelectTrigger className="w-36">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="Yes">Has access</SelectItem>
                <SelectItem value="No">No access</SelectItem>
            </SelectContent>
        </Select>
    );
}

function useDirectory<T>(
    active: boolean,
    load: (
        search: string,
        page: number,
        filter: AccessFilter
    ) => Promise<{ data: T[]; rows: number }>
) {
    const [filter, setFilter] = useState<AccessFilter>('All');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [rows, setRows] = useState<T[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!active) return;
        let cancelled = false;
        setLoading(true);
        const timer = setTimeout(
            () =>
                load(search, page, filter)
                    .then((result) => {
                        if (cancelled) return;
                        setRows(result.data);
                        setTotal(result.rows);
                        setFailed(false);
                    })
                    .catch(() => !cancelled && setFailed(true))
                    .finally(() => !cancelled && setLoading(false)),
            search ? 250 : 0
        );
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [active, search, page, filter]);

    return {
        search,
        setSearch: (value: string) => {
            setSearch(value);
            setPage(1);
        },
        filter,
        setFilter: (value: AccessFilter) => {
            setFilter(value);
            setPage(1);
        },
        page,
        setPage,
        rows,
        total,
        loading,
        failed
    };
}

interface DashboardAccessEditorProps {
    dashboardId: string;
    active: boolean;
    userIds: Set<string>;
    groupIds: Set<string>;
    organizationAccess: boolean;
    onUserIdsChange: (ids: Set<string>) => void;
    onGroupIdsChange: (ids: Set<string>) => void;
    onOrganizationAccessChange: (access: boolean) => void;
    showOrganizationNotice?: boolean;
    scrollList?: boolean;
}

export function DashboardAccessEditor({
    dashboardId,
    active,
    userIds,
    groupIds,
    organizationAccess,
    onUserIdsChange: setUserIds,
    onGroupIdsChange: setGroupIds,
    onOrganizationAccessChange: setOrganizationAccess,
    showOrganizationNotice = false,
    scrollList = false
}: DashboardAccessEditorProps) {
    const listTabClassName = cn(
        scrollList &&
            'flex flex-col flex-1 min-h-0 data-[state=inactive]:hidden'
    );
    const listClassName = cn(scrollList && 'flex-1 min-h-0 overflow-y-auto');
    const [tab, setTab] = useState('users');

    const users = useDirectory<AccessUser>(active, (search, page, filter) =>
        listAccessUsers(search, page, PAGE_SIZE, dashboardId, filter)
    );
    const groups = useDirectory<AccessGroup>(active, (search, page, filter) =>
        listAccessGroups(search, page, PAGE_SIZE, dashboardId, filter)
    );

    const toggle = (
        set: Set<string>,
        update: (next: Set<string>) => void,
        id: string,
        on: boolean
    ) => {
        const next = new Set(set);
        if (on) next.add(id);
        else next.delete(id);
        update(next);
    };

    return (
        <Tabs
            value={tab}
            onValueChange={setTab}
            className={cn(scrollList && 'flex flex-col flex-1 min-h-0')}
        >
            <PillTabsList tabs={TABS} underline className="mb-5 shrink-0" />

            <TabsContent value="users" className={listTabClassName}>
                {showOrganizationNotice && <OrganizationArtifactsNotice />}
                <div className="flex items-center gap-3 mb-4">
                    <SearchBar
                        value={users.search}
                        onChange={users.setSearch}
                        placeholder="Search users..."
                        className="flex-1 max-w-none"
                    />
                    <FilterSelect
                        value={users.filter}
                        onChange={users.setFilter}
                    />
                </div>
                <div className={listClassName}>
                    {users.loading ? (
                        <TableSkeleton columns={4} />
                    ) : users.failed ? (
                        <p className="py-6 text-destructive text-sm">
                            The users could not be loaded
                        </p>
                    ) : (
                        <Table variant="fluid" className="table-fixed">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-14">View</TableHead>
                                    <TableHead>Display Name</TableHead>
                                    <TableHead>Email</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={3}
                                            className="py-8 text-muted-foreground text-center"
                                        >
                                            No users found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.rows.map((user) => (
                                        <TableRow
                                            key={user.userId}
                                            className="cursor-pointer"
                                            onClick={() =>
                                                toggle(
                                                    userIds,
                                                    setUserIds,
                                                    user.userId,
                                                    !userIds.has(user.userId)
                                                )
                                            }
                                        >
                                            <TableCell>
                                                <Checkbox
                                                    variant="fluid"
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                    checked={userIds.has(
                                                        user.userId
                                                    )}
                                                    onCheckedChange={(on) =>
                                                        toggle(
                                                            userIds,
                                                            setUserIds,
                                                            user.userId,
                                                            on === true
                                                        )
                                                    }
                                                    aria-label={`View access for ${user.displayName}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-foreground truncate">
                                                {user.displayName}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground truncate">
                                                {user.email}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>
                <Pager
                    page={users.page}
                    total={users.total}
                    onPageChange={users.setPage}
                />
            </TabsContent>

            <TabsContent value="groups" className={listTabClassName}>
                {showOrganizationNotice && <OrganizationArtifactsNotice />}
                <div className="flex items-center gap-3 mb-4">
                    <SearchBar
                        value={groups.search}
                        onChange={groups.setSearch}
                        placeholder="Search groups..."
                        className="flex-1 max-w-none"
                    />
                    <FilterSelect
                        value={groups.filter}
                        onChange={groups.setFilter}
                    />
                </div>
                <div className={listClassName}>
                    {groups.loading ? (
                        <TableSkeleton columns={2} />
                    ) : groups.failed ? (
                        <p className="py-6 text-destructive text-sm">
                            The groups could not be loaded
                        </p>
                    ) : (
                        <Table variant="fluid" className="table-fixed">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-14">View</TableHead>
                                    <TableHead>Group</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groups.rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={2}
                                            className="py-8 text-muted-foreground text-center"
                                        >
                                            No groups found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    groups.rows.map((group) => (
                                        <TableRow
                                            key={group.groupId}
                                            className="cursor-pointer"
                                            onClick={() =>
                                                toggle(
                                                    groupIds,
                                                    setGroupIds,
                                                    group.groupId,
                                                    !groupIds.has(group.groupId)
                                                )
                                            }
                                        >
                                            <TableCell>
                                                <Checkbox
                                                    variant="fluid"
                                                    onClick={(event) =>
                                                        event.stopPropagation()
                                                    }
                                                    checked={groupIds.has(
                                                        group.groupId
                                                    )}
                                                    onCheckedChange={(on) =>
                                                        toggle(
                                                            groupIds,
                                                            setGroupIds,
                                                            group.groupId,
                                                            on === true
                                                        )
                                                    }
                                                    aria-label={`View access for ${group.groupName}`}
                                                />
                                            </TableCell>
                                            <TableCell className="text-foreground truncate">
                                                {group.groupName}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </div>
                <Pager
                    page={groups.page}
                    total={groups.total}
                    onPageChange={groups.setPage}
                />
            </TabsContent>

            <TabsContent value="organization">
                <label className="flex justify-between items-center gap-4 px-1 py-3">
                    <span>
                        <span className="block font-medium text-foreground text-base">
                            Everyone in your organization
                        </span>
                        <span className="block text-muted-foreground text-sm">
                            Everyone in your organization has access while it is
                            shared.
                        </span>
                    </span>
                    <Switch
                        checked={organizationAccess}
                        onCheckedChange={setOrganizationAccess}
                    />
                </label>
            </TabsContent>
        </Tabs>
    );
}
